/** The qualification calculators of the deal form (design 9.3). No network and no React. */

export const LB_PER_GAL = 7.6;
export const COOKING_LOSS = 0.25;
export const DEFAULT_SCRAP = 0.2;
export const GOOD_ROUTE_VALUE = 4;
const ROTATION_WEEKS = 4;

const round = (value: number, places = 1) => Math.round(value * 10 ** places) / 10 ** places;

/** Gallons of fresh oil in the fryers each week: fryers × changes × pounds ÷ 7.6 lb/gal. */
export const fryerVolume = (fryers: number, changesPerWeek: number, fryerLb: number) => (fryers * changesPerWeek * fryerLb) / LB_PER_GAL;

/** The used oil that comes back after the cooking loss. */
export const collectableVolume = (fryerGal: number, loss = COOKING_LOSS) => fryerGal * (1 - loss);

/** The raw gallons in the container: the collectable oil is the net part after the scrap. */
export const rawVolume = (collectableGal: number, scrap = DEFAULT_SCRAP) => collectableGal / (1 - scrap);

export interface Volumes {
  fryer: number;
  collectable: number;
  raw: number;
}

/** All three volumes, in tenths of a gallon each week. Null until the three inputs are numbers. */
export function volumes(fryers: number | null, changesPerWeek: number | null, fryerLb: number | null): Volumes | null {
  if (fryers === null || changesPerWeek === null || fryerLb === null) return null;
  const fryer = fryerVolume(fryers, changesPerWeek, fryerLb);
  const collectable = collectableVolume(fryer);
  return { fryer: round(fryer), collectable: round(collectable), raw: round(rawVolume(collectable)) };
}

/** The longest gap in weeks between two visits of the 4-week rotation, as on the server. */
export function cycleWeeks(weekGroup: string | null): number {
  if (!weekGroup || !/^[1-4](-[1-4]){0,3}$/.test(weekGroup)) return ROTATION_WEEKS;
  const weeks = [...new Set(weekGroup.split("-").map(Number))].sort((a, b) => a - b);
  const gaps = weeks.slice(1).map((week, i) => week - weeks[i]);
  return Math.max(...gaps, weeks[0] + ROTATION_WEEKS - weeks[weeks.length - 1]);
}

export interface Recommendation {
  need: number;
  sizes: number[];
}

/** Design 8.6: the smallest size, else the smallest pair, that holds rate × weeks × (1 + margin). */
export function recommendedSize(rate: number, weeks: number, margin: number, sizes: readonly number[]): Recommendation | null {
  const ordered = [...new Set(sizes)].sort((a, b) => a - b);
  if (rate <= 0 || weeks <= 0 || !ordered.length) return null;
  const need = round(rate * weeks * (1 + margin), 2);
  const single = ordered.find((size) => size >= need);
  if (single !== undefined) return { need, sizes: [single] };
  let best: [number, number, number] | null = null;
  ordered.forEach((small, i) => {
    for (const large of ordered.slice(i)) {
      const total = small + large;
      if (total >= need && (!best || total < best[0] || (total === best[0] && large < best[1]))) best = [total, large, small];
    }
  });
  const pair = best as [number, number, number] | null;
  return { need, sizes: pair ? [pair[2], pair[1]] : [] };
}

/** Our rebate each week against a competitor's rate on their yield of the same oil. */
export function rebateCompare(netGal: number, ourRate: number, theirRate: number, theirYield: number) {
  const ours = netGal * ourRate;
  const theirs = netGal * theirYield * theirRate;
  return { ours: round(ours, 2), theirs: round(theirs, 2), better: ours >= theirs };
}

/** Gallons for each mile of extra driving. More than 4 is a good stop. */
export function routeValue(galPerVisit: number, extraMiles: number) {
  if (extraMiles <= 0) return { value: null, good: true };
  const value = round(galPerVisit / extraMiles);
  return { value, good: value > GOOD_ROUTE_VALUE };
}
